/**
 * Artifact route — `/w/{slug}/c/{canvasId}/a/{artifactId}`.
 *
 * Sits underneath the canvas route in the URL hierarchy and inherits
 * the same chat panel. The main pane swaps from `CanvasViewer` to
 * `ArtifactPanel`; breadcrumbs append the artifact title with its
 * file-type icon.
 *
 * Conversation context: artifact-edit chat lives in the same thread
 * the artifact was created in (or any thread on the same canvas the
 * user picks). The chat panel reads `useActiveArtifact()` for context
 * injection — see the chat panel for how the agent's prompt picks up
 * the artifact id, format, version.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { z } from "zod";

import { useApi } from "../../api";
import { ensureArtifactChatThread, getArtifact } from "../../api/artifacts";
import { ArtifactPanel } from "../../components/artifact/ArtifactPanel";
import { useActiveThread } from "../../lib/active-thread";
import { useDocumentTitle } from "@/lib/document-title";
import { canonicalSlug, routeWorkspace } from "../../lib/routeWorkspace";

const artifactSearchSchema = z.object({
  // Conversation pin — when present, the chat panel binds THIS thread
  // instead of the artifact's newest `chat_thread_id`. Set by the
  // conversation switcher (canvas route) when the user reopens an
  // older conversation over this artifact.
  conversation: z.string().optional(),
  // Version pin — when present, the artifact panel shows the older
  // version instead of `current_version`. Useful for "look at v2"
  // links shared between teammates.
  version: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/_app/w/$slug/c/$canvasId/a/$artifactId")({
  validateSearch: artifactSearchSchema,
  component: ArtifactPage,
});

function ArtifactPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { slug, canvasId, artifactId } = useParams({
    from: "/_app/w/$slug/c/$canvasId/a/$artifactId",
  });
  const search = useSearch({ from: "/_app/w/$slug/c/$canvasId/a/$artifactId" });
  const { conversation: pinnedThreadId, version: pinnedVersion } = search;
  const navigate = useNavigate();
  const { setActiveThread } = useActiveThread();

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });

  // The artifact carries its own chat thread id (or null = not yet
  // forked). We fetch the artifact eagerly so the chat panel can
  // bind to the right thread on first paint.
  const artifactQuery = useQuery({
    queryKey: ["artifact", artifactId],
    queryFn: () => getArtifact(artifactId),
  });
  const artifact = artifactQuery.data?.artifact ?? null;
  const chatThreadId = artifact?.chat_thread_id ?? null;
  const originThreadId = artifact?.thread_id ?? null;

  // The slug in the URL can be stale (lib/routeWorkspace.ts): match it, and
  // otherwise use the workspace the artifact itself belongs to. This page
  // matched by slug only, so `/w/demo-oxera/c/…/a/…` — a slug mojo had
  // suffixed to `demo-oxera-1` — rendered the deck with a chat bound to no
  // workspace at all. Same fallback as the canvas route.
  const workspace = routeWorkspace(workspaces.data, slug, artifact?.workspace_id);

  // …and put the real slug in the URL, keeping the conversation and the
  // version pin, so an old link or stored path recovers in place. The canvas
  // route above skips its own redirect on this subroute (it would leave the
  // artifact), so this one is the artifact page's.
  const canonical = canonicalSlug(workspace, slug);
  useEffect(() => {
    if (!canonical) return;
    void navigate({
      to: "/w/$slug/c/$canvasId/a/$artifactId",
      params: { slug: canonical, canvasId, artifactId },
      search: { conversation: search.conversation, version: search.version },
      replace: true,
    });
  }, [canonical, canvasId, artifactId, navigate, search.conversation, search.version]);

  // Binding priority for the chat panel:
  //   1. `?conversation=` pin — an OLDER conversation over this artifact,
  //      reopened from the switcher. Without the pin, old artifact
  //      conversations were unreachable: this route always bound
  //      `chat_thread_id`, which `fresh` repoints at the newest thread.
  //   2. `artifact.chat_thread_id` — the artifact's current thread.
  //   3. null — draft mode (no thread yet; first send creates one).
  const boundThreadId = pinnedThreadId ?? chatThreadId;

  // The tab: the artifact (and the older version, when the link pins one),
  // then its canvas — the same ["canvas", id] query the parent route runs.
  const canvasName = useQuery({
    queryKey: ["canvas", canvasId],
    queryFn: () => api.getCanvas(canvasId),
  }).data?.canvas.name;
  useDocumentTitle([
    artifact ? `${artifact.title}${pinnedVersion ? ` (v${pinnedVersion})` : ""}` : "Artifact",
    canvasName ?? workspace?.name,
  ]);

  // Lazy-create the artifact's chat thread if it doesn't exist yet.
  // The chat panel calls `onDraftPromoted` after the user sends their
  // first message; we hand it a creator that hits POST /artifacts/
  // {id}/chat-thread so the new thread is forked from the artifact's
  // origin conversation (parent_thread_id) and pinned to the
  // artifact (artifact_id) — both required for the agent runtime
  // and the canvas-thread-list filter respectively.
  const ensureThread = useMutation({
    mutationFn: () => ensureArtifactChatThread(artifactId, originThreadId),
    onSuccess: (thread) => {
      void queryClient.invalidateQueries({ queryKey: ["artifact", artifactId] });
      void queryClient.invalidateQueries({
        queryKey: ["canvas-artifacts", canvasId],
      });
      // Re-bind the chat to the freshly-created thread.
      setActiveThread({
        threadId: thread.id,
        canvasId,
        workspaceId: workspace?.id ?? null,
        draft: false,
      });
    },
  });

  // Hand-rolled draft-promotion: when the user types their first
  // message in this artifact (chatThreadId is null), the chat panel
  // calls into our handler — we materialise the thread server-side
  // and let the panel finish posting to it.
  const handleDraftPromoted = useCallback(
    async () => {
      const thread = await ensureThread.mutateAsync();
      return thread.id;
    },
    // ensureThread is referentially stable from useMutation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Bind the resolved thread for the chat panel (pin > chat_thread_id —
  // see `boundThreadId` above). Null means "no thread yet — draft
  // mode"; the panel shows the empty composer and calls
  // `onDraftPromoted` on first send.
  useEffect(() => {
    setActiveThread({
      threadId: boundThreadId,
      canvasId,
      workspaceId: workspace?.id ?? null,
      draft: boundThreadId === null,
      onDraftPromoted: boundThreadId === null
        ? (newThreadId: string) => {
            // ChatPanel passes the new thread id after a successful
            // POST. We just need to invalidate so the artifact row
            // refetches with chat_thread_id populated.
            void newThreadId;
            void queryClient.invalidateQueries({
              queryKey: ["artifact", artifactId],
            });
          }
        : undefined,
    });
    return () =>
      setActiveThread({
        threadId: null,
        canvasId: null,
        workspaceId: null,
        draft: false,
      });
  }, [boundThreadId, canvasId, workspace?.id, setActiveThread, queryClient, artifactId]);

  // ChatPanel's lazy thread creation talks to `/workspaces/.../threads`
  // by default. For artifact chats we want it to call
  // `ensureArtifactChatThread` instead. We expose that via a window
  // hook the panel checks before falling back to the workspace path.
  useEffect(() => {
    // Register only in true draft mode (no pin AND no chat thread) —
    // ChatPanel consults the creator only when no thread is bound, so
    // the gate mirrors `draft` above.
    if (boundThreadId !== null) return;
    const handler = async (): Promise<string> => {
      const t = await ensureThread.mutateAsync();
      return t.id;
    };
    (window as unknown as {
      __chaiArtifactDraftCreator?: () => Promise<string>;
    }).__chaiArtifactDraftCreator = handler;
    return () => {
      delete (window as unknown as {
        __chaiArtifactDraftCreator?: () => Promise<string>;
      }).__chaiArtifactDraftCreator;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundThreadId]);

  // "New conversation" over THIS artifact. The conversation switcher
  // lives in the parent canvas route's chat-header slot; its default
  // start-draft action navigates to the canvas draft URL — which would
  // bounce the user off the artifact editor. Instead the canvas route
  // calls this hook when the artifact child is active: mint a brand-new
  // artifact chat thread (fresh=true repoints artifact.chat_thread_id
  // server-side; the old thread survives in the switcher's artifact
  // list), then invalidate the artifact query — the thread-binding
  // effect above re-binds the new empty thread automatically.
  //
  // Registered ALWAYS while this route is mounted (unlike the draft
  // creator above, which only exists while chatThreadId is null).
  useEffect(() => {
    const startFresh = async (): Promise<void> => {
      await ensureArtifactChatThread(artifactId, null, true);
      await queryClient.invalidateQueries({ queryKey: ["artifact", artifactId] });
      // If an OLDER conversation is pinned via `?conversation=`, drop
      // the pin (keeping other search params, e.g. `version`) so the
      // binding effect above re-binds the freshly-minted
      // `chat_thread_id` instead of staying on the pinned old thread.
      if (pinnedThreadId) {
        void navigate({
          to: "/w/$slug/c/$canvasId/a/$artifactId",
          params: { slug, canvasId, artifactId },
          search: (prev) => ({ ...prev, conversation: undefined }),
          replace: true,
        });
      }
    };
    (window as unknown as {
      __chaiArtifactStartFresh?: () => Promise<void>;
    }).__chaiArtifactStartFresh = startFresh;
    return () => {
      delete (window as unknown as {
        __chaiArtifactStartFresh?: () => Promise<void>;
      }).__chaiArtifactStartFresh;
    };
  }, [artifactId, queryClient, pinnedThreadId, navigate, slug, canvasId]);

  // We deliberately don't render a ConversationSwitcher inside the
  // artifact route — the parent canvas route renders it (its artifact
  // rows navigate here with a `?conversation=` pin).
  void handleDraftPromoted;

  const _setVariable = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.setVariableValue(canvasId, key, value),
  });
  void _setVariable;

  return <ArtifactPanel artifactId={artifactId} />;
}
